// Fetch wrapper for the all-orders worker /api/* surface.
//
// The /api/* routes do NOT use a uniform envelope — each returns a bespoke
// shape (e.g. { records }, { results }, { success, ... }, raw arrays) and
// signals errors with a non-2xx status, usually with { error } in the body.
// We surface non-2xx as ApiError and otherwise return the parsed JSON as-is so
// callers can read whatever shape the legacy client read.

export class ApiError extends Error {
  status: number;
  details: unknown;
  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

const API_BASE = ""; // same-origin: vite proxies /api in dev; worker serves it in prod

/** GET/POST the worker /api/* surface. Returns parsed JSON; throws ApiError on non-2xx. */
export async function apiCall<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}/api/${path.replace(/^\//, "")}`, {
    credentials: "include",
    headers:
      init?.body && typeof init.body === "string"
        ? { "content-type": "application/json", ...(init?.headers || {}) }
        : { ...(init?.headers || {}) },
    ...init,
  });
  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status, text.slice(0, 300));
    return text as unknown as T;
  }
  if (!res.ok) {
    const msg = (body as { error?: string } | null)?.error || `HTTP ${res.status}`;
    throw new ApiError(msg, res.status, body);
  }
  return body as T;
}

/** Convenience POST/PATCH/DELETE with an optional JSON body. */
export function apiSend<T = unknown>(
  path: string,
  method: "POST" | "PATCH" | "DELETE" | "PUT",
  body?: unknown,
): Promise<T> {
  return apiCall<T>(path, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}
